// ============================================================================
// PLAYER ACTION ANIMATION COMPONENT
// ============================================================================
// Shows item flying from sender to target, then impact animation
// Sound files are in /public/sounds/items/
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { ActiveAnimation } from '../types/gameItemsTypes';
import './PlayerActionAnimation.css';

interface Position {
  x: number;
  y: number;
}

interface PlayerActionAnimationProps {
  animation: ActiveAnimation;
  targetPosition?: Position;
  senderPosition?: Position;
  onComplete?: (animationId: string) => void;
  // Fired the moment the projectile lands - drives the target avatar's
  // hit reaction (seat shake) in the table SVG.
  onImpact?: (targetId: string) => void;
}

const PlayerActionAnimation: React.FC<PlayerActionAnimationProps> = ({
  animation,
  targetPosition,
  senderPosition,
  onComplete,
  onImpact,
}) => {
  const [phase, setPhase] = useState<'flying' | 'impact' | 'done'>('flying');
  const [showToast, setShowToast] = useState(true);
  const [currentPos, setCurrentPos] = useState<Position | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const soundPlayedRef = useRef(false); // Prevent double sound play

  const item = animation.item;
  // Taunts FLY too - "You Suck" is a thrown insult, not a status toast.
  const isAnnounce = item.category === 'announce';
  const isThrowable = (item.category === 'throwable' || item.category === 'taunt') && !isAnnounce;
  const isSelfAction = animation.senderId === animation.targetId;

  // Some taunts throw a different projectile than their catalog emoji -
  // YOU SUCK throws exactly what you think it throws.
  const PROJECTILE_OVERRIDES: { [id: string]: string } = {
    taunt_you_suck: '\u{1F4A9}',
    taunt_donk: '\u{1F434}',
    taunt_bad_beat: '\u{1F480}',
  };
  const projectileEmoji = PROJECTILE_OVERRIDES[animation.itemId] || item.emoji;

  // Per-item SPLATTER: residue that STICKS on the target and drips before
  // fading with the animation. color = the splat blobs, sticker = an
  // emoji smear left behind, drips = falling droplets.
  const SPLATS: { [id: string]: { color: string; sticker?: string; drips: number } } = {
    tomato: { color: 'rgba(220, 38, 38, 0.92)', drips: 3 },
    egg: { color: 'rgba(253, 224, 71, 0.95)', sticker: '\u{1F373}', drips: 2 },
    taunt_you_suck: { color: 'rgba(120, 72, 20, 0.95)', sticker: '\u{1F4A9}', drips: 3 },
    taunt_bad_beat: { color: 'rgba(148, 163, 184, 0.85)', sticker: '\u{1F480}', drips: 0 },
    beer: { color: 'rgba(251, 191, 36, 0.85)', sticker: '\u{1F37A}', drips: 3 },
    fish: { color: 'rgba(56, 189, 248, 0.75)', sticker: '\u{1F41F}', drips: 2 },
    kiss: { color: 'rgba(244, 114, 182, 0.85)', sticker: '\u{1F48B}', drips: 0 },
    rocket: { color: 'rgba(251, 146, 60, 0.9)', sticker: '\u{1F4A5}', drips: 0 },
  };
  const splat = SPLATS[animation.itemId];
  
  // Convert SVG (viewBox) coordinates to screen coordinates.
  // The old version hardcoded the CLASSIC felt's container and 700x620
  // viewBox - the premium felt (every live table) uses a different element
  // and different viewBoxes per orientation, so lookups failed and throws
  // landed mid-screen. getScreenCTM is the browser's own exact transform:
  // it accounts for viewBox, letterboxing, and orientation on any felt.
  const svgToScreen = (svgPos: Position | undefined): Position | null => {
    if (!svgPos) return null;

    const svgEl = document.querySelector('.premium-table-svg, .poker-table-svg') as SVGSVGElement | null;
    if (!svgEl) return null;

    try {
      const ctm = svgEl.getScreenCTM();
      if (ctm) {
        const pt = new DOMPoint(svgPos.x, svgPos.y).matrixTransform(ctm);
        return { x: pt.x, y: pt.y };
      }
    } catch { /* fall through to manual math */ }

    // Manual fallback: xMidYMid meet = uniform scale, centered letterbox
    const rect = svgEl.getBoundingClientRect();
    const vb: any = (svgEl as any).viewBox?.baseVal;
    const vbX = vb?.x ?? 0;
    const vbY = vb?.y ?? 0;
    const vbW = vb?.width || 700;
    const vbH = vb?.height || 620;
    const s = Math.min(rect.width / vbW, rect.height / vbH);
    const offX = rect.left + (rect.width - vbW * s) / 2;
    const offY = rect.top + (rect.height - vbH * s) / 2;
    return { x: offX + (svgPos.x - vbX) * s, y: offY + (svgPos.y - vbY) * s };
  };

  // Get screen positions
  const senderScreenPos = svgToScreen(senderPosition);
  const targetScreenPos = svgToScreen(targetPosition);

  console.log('🎬 [Animation] Rendering:', animation.itemId, {
    senderPosition,
    targetPosition,
    senderScreenPos,
    targetScreenPos,
    phase
  });

  // Initialize position
  useEffect(() => {
    if (isAnnounce) {
      setCurrentPos({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
      setPhase('impact'); // marquee renders immediately, full duration
    } else if (isThrowable && senderScreenPos) {
      setCurrentPos(senderScreenPos);
    } else if (targetScreenPos) {
      setCurrentPos(targetScreenPos);
      setPhase('impact');
    } else {
      // Fallback to center
      setCurrentPos({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
      setPhase('impact');
    }
  }, []);

  // CINEMATIC FLIGHT: quadratic-bezier ARC from the thrower's seat to the
  // target's avatar with spin - not a straight slide. ~700ms, apex lifted
  // above the midpoint, rotation + slight scale-up mid-flight.
  const [spin, setSpin] = useState(0);
  const [flightScale, setFlightScale] = useState(1);
  useEffect(() => {
    if (phase !== 'flying' || !isThrowable || !targetScreenPos) return;
    const from = senderScreenPos || { x: window.innerWidth / 2, y: window.innerHeight - 80 };
    const to = targetScreenPos;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const apex = {
      x: (from.x + to.x) / 2,
      y: Math.min(from.y, to.y) - Math.min(180, dist * 0.35 + 60),
    };
    const FLIGHT_MS = 700;
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / FLIGHT_MS);
      const u = 1 - t;
      // Quadratic bezier through the lifted apex
      const x = u * u * from.x + 2 * u * t * apex.x + t * t * to.x;
      const y = u * u * from.y + 2 * u * t * apex.y + t * t * to.y;
      setCurrentPos({ x, y });
      setSpin(t * 720);
      setFlightScale(1 + Math.sin(t * Math.PI) * 0.5);
      if (t < 1) raf = requestAnimationFrame(step);
      else setPhase('impact');
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, isThrowable]);

  // End animation after duration
  useEffect(() => {
    const totalDuration = isThrowable ? item.duration + 600 : item.duration;
    
    const endTimer = setTimeout(() => {
      setPhase('done');
      if (onComplete) onComplete(animation.id);
    }, totalDuration);

    // Hide toast after 3 seconds
    const toastTimer = setTimeout(() => {
      setShowToast(false);
    }, 3000);

    return () => {
      clearTimeout(endTimer);
      clearTimeout(toastTimer);
    };
  }, [animation.id, item.duration, isThrowable, onComplete]);

  // Impact ripple to the table: avatar hit reaction
  useEffect(() => {
    if (phase === 'impact' && onImpact) onImpact(animation.targetId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Play sound on impact (only once)
  useEffect(() => {
    if (phase === 'impact' && item.soundFile && !soundPlayedRef.current) {
      soundPlayedRef.current = true;
      try {
        // Sound files are in /public/sounds/items/
        const audio = new Audio(`/sounds/items/${item.soundFile}`);
        audio.volume = 0.6;
        audio.play().catch((err) => {
          console.log('🔇 Sound play failed:', err.message);
        });
      } catch (e) {
        // Ignore sound errors silently
        console.log('🔇 Sound error:', e);
      }
    }
  }, [phase, item.soundFile]);

  if (phase === 'done' || !currentPos) return null;

  // 📢 PAID ANNOUNCEMENT: a cinematic table-wide marquee. The line IS the
  // product - full-width gold band, megaphone pulse, sender attribution.
  if (isAnnounce) {
    const line = (item.message || '')
      .replace(/\{target\}/g, animation.targetName)
      .replace(/\{sender\}/g, animation.senderName);
    return (
      <div style={{
        position: 'fixed', top: '30%', left: 0, right: 0, zIndex: 9450,
        pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        <style>{`
          @keyframes pssAnnIn {
            0% { opacity: 0; transform: scaleX(0.2); }
            60% { opacity: 1; transform: scaleX(1.03); }
            100% { opacity: 1; transform: scaleX(1); }
          }
          @keyframes pssAnnText {
            0% { opacity: 0; transform: translateY(14px) scale(1.25); }
            35% { opacity: 0; }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes pssAnnHorn {
            0%, 100% { transform: rotate(-12deg) scale(1); }
            50% { transform: rotate(10deg) scale(1.18); }
          }
          @keyframes pssAnnOut { to { opacity: 0; } }
        `}</style>
        <div style={{
          width: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(20, 8, 8, 0.94) 12%, rgba(20, 8, 8, 0.94) 88%, transparent)',
          borderTop: '1px solid rgba(212, 175, 55, 0.5)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.5)',
          padding: 'clamp(16px, 4vw, 24px) 14px',
          textAlign: 'center',
          transformOrigin: 'center',
          animation: `pssAnnIn 500ms cubic-bezier(0.16, 1, 0.3, 1), pssAnnOut 600ms ease ${Math.max(0, item.duration - 600)}ms forwards`,
        }}>
          <div style={{ fontSize: 'clamp(26px, 7vw, 36px)', display: 'inline-block', animation: 'pssAnnHorn 900ms ease infinite' }}>
            {item.emoji}
          </div>
          <div style={{
            color: '#fff', fontSize: 'clamp(17px, 4.8vw, 26px)', fontWeight: 900,
            letterSpacing: '0.03em', marginTop: 6, textShadow: '0 2px 18px rgba(212,175,55,0.5)',
            animation: 'pssAnnText 900ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
            {line}
          </div>
          <div style={{ color: '#d4af37', fontSize: 'clamp(11px, 3vw, 13px)', fontWeight: 800, marginTop: 8, letterSpacing: '0.18em' }}>
            — PAID ANNOUNCEMENT FROM {animation.senderName.toUpperCase()} —
          </div>
        </div>
      </div>
    );
  }

  // Get toast text
  const getToastText = () => {
    if (isSelfAction) {
      return (
        <>
          <strong>{animation.senderName}</strong> used {item.name}!
        </>
      );
    }
    return (
      <>
        <strong>{animation.senderName}</strong> threw {item.name} at <strong>{animation.targetName}</strong>!
      </>
    );
  };

  return (
    <>
      {/* Toast notification at top */}
      {showToast && (
        <div className="item-toast">
          <span className="toast-emoji">{item.emoji}</span>
          <span className="toast-text">{getToastText()}</span>
        </div>
      )}
      
      {/* Flying/Impact emoji */}
      <style>{`
        @keyframes pssImpactRing {
          0% { transform: translate(-50%, -50%) scale(0.3); opacity: 0.95; }
          100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
        }
        @keyframes pssImpactSquash {
          0% { transform: scale(1.9, 0.55); }
          40% { transform: scale(0.8, 1.25); }
          70% { transform: scale(1.15, 0.9); }
          100% { transform: scale(1, 1); }
        }
      `}</style>
      <div
        ref={containerRef}
        className={`flying-emoji ${phase} ${animation.itemId}`}
        style={{
          left: currentPos.x,
          top: currentPos.y,
          transition: 'none',
        }}
      >
        {/* WHO THREW IT - tag rides with the projectile */}
        {phase === 'flying' && !isSelfAction && (
          <div style={{
            position: 'absolute', top: -26, left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(6,10,18,0.92)', border: '1px solid rgba(212,175,55,0.55)',
            borderRadius: 10, padding: '2px 9px', whiteSpace: 'nowrap',
            color: '#fde68a', fontSize: 11, fontWeight: 800, pointerEvents: 'none',
          }}>{animation.senderName}</div>
        )}
        {phase === 'impact' && (
          <span style={{
            position: 'absolute', left: '50%', top: '50%', width: 74, height: 74,
            border: '3px solid rgba(255,255,255,0.85)', borderRadius: '50%',
            pointerEvents: 'none', animation: 'pssImpactRing 500ms ease-out forwards',
          }} />
        )}
        <span
          className="emoji-icon"
          style={phase === 'flying'
            ? { display: 'inline-block', transform: `rotate(${spin}deg) scale(${flightScale})` }
            : { display: 'inline-block', animation: 'pssImpactSquash 550ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        >{projectileEmoji}</span>

        {/* 💥 SPLATTER RESIDUE - sticks on the avatar, drips, fades out */}
        {phase === 'impact' && splat && (
          <div style={{ position: 'absolute', left: '50%', top: '50%', pointerEvents: 'none' }}>
            <style>{`
              @keyframes pssSplatIn { 0% { transform: scale(0.2); opacity: 0; } 30% { transform: scale(1.25); opacity: 1; } 60% { transform: scale(1); } 100% { transform: scale(1); opacity: 0; } }
              @keyframes pssDrip { 0% { transform: translateY(0); opacity: 0.95; } 100% { transform: translateY(46px); opacity: 0; } }
              @keyframes pssStickerFade { 0% { opacity: 0; transform: scale(1.6) rotate(-14deg); } 25% { opacity: 0.95; transform: scale(1) rotate(-14deg); } 80% { opacity: 0.8; } 100% { opacity: 0; } }
            `}</style>
            {/* Irregular splat: overlapping blobs */}
            {[
              { x: -14, y: -12, r: 20 }, { x: 12, y: -4, r: 16 }, { x: -2, y: 10, r: 18 },
              { x: 20, y: 12, r: 10 }, { x: -24, y: 8, r: 9 },
            ].map((b, i) => (
              <span key={i} style={{
                position: 'absolute', left: b.x - b.r, top: b.y - b.r,
                width: b.r * 2, height: b.r * 2, borderRadius: '50%',
                background: `radial-gradient(circle at 38% 32%, ${splat.color}, transparent 74%)`,
                animation: `pssSplatIn ${Math.max(1200, item.duration)}ms ease forwards`,
              }} />
            ))}
            {Array.from({ length: splat.drips }).map((_, i) => (
              <span key={`d${i}`} style={{
                position: 'absolute', left: -14 + i * 14, top: 12,
                width: 7, height: 15, borderRadius: '40% 40% 60% 60%',
                background: splat.color,
                animation: `pssDrip ${900 + i * 260}ms ease-in ${140 + i * 120}ms forwards`, opacity: 0,
              }} />
            ))}
            {splat.sticker && (
              <span style={{
                position: 'absolute', left: -15, top: -18, fontSize: 30,
                animation: `pssStickerFade ${Math.max(1400, item.duration)}ms ease forwards`,
              }}>{splat.sticker}</span>
            )}
          </div>
        )}

        {/* Taunt STAMP - the insult slams over the impact point */}
        {phase === 'impact' && item.category === 'taunt' && (
          <div style={{
            position: 'absolute', left: '50%', top: -46, transform: 'translateX(-50%) rotate(-7deg)',
            pointerEvents: 'none', whiteSpace: 'nowrap',
            color: '#ef4444', fontSize: 'clamp(19px, 5vw, 26px)', fontWeight: 900,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            textShadow: '0 0 4px rgba(0,0,0,0.9), 0 0 22px rgba(239,68,68,0.6)',
            WebkitTextStroke: '1px rgba(0,0,0,0.6)',
            animation: 'pssImpactSquash 500ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}>{item.name}</div>
        )}
        
        {/* Impact particles */}
        {phase === 'impact' && (
          <div className="impact-particles">
            {(animation.itemId === 'tomato' || animation.itemId === 'egg') && (
              <span className="splat-particle"></span>

            )}
            {animation.itemId === 'money' && (
              <>
                <span className="fly-particle p1"></span>

                <span className="fly-particle p2"></span>

                <span className="fly-particle p3"></span>

              </>
            )}
            {animation.itemId === 'trophy' && (
              <>
                <span className="fly-particle p1"></span>

                <span className="fly-particle p2"></span>

                <span className="fly-particle p3"></span>

              </>
            )}
            {animation.itemId === 'fire' && (
              <>
                <span className="fly-particle p1"></span>

                <span className="fly-particle p2"></span>

              </>
            )}
            {animation.itemId === 'kiss' && (
              <>
                <span className="fly-particle p1"></span>

                <span className="fly-particle p2"></span>

              </>
            )}
            {animation.itemId === 'beer' && (
              <>
                <span className="fly-particle p1"></span>

                <span className="fly-particle p2"></span>

              </>
            )}
            {animation.itemId === 'rocket' && (
              <>
                <span className="fly-particle p1">⭐</span>
                <span className="fly-particle p2"></span>

                <span className="fly-particle p3"></span>

              </>
            )}
            {animation.itemId === 'lucky' && (
              <>
                <span className="fly-particle p1"></span>

                <span className="fly-particle p2"></span>

                <span className="fly-particle p3">⭐</span>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PlayerActionAnimation;